import { LitElement, html, css, nothing, render } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import { KatapultButton } from './katapult-button.js';
import './katapult-scroll.js';

import { GetEventCoords } from '../../modules/GetEventCoords.js';
import { Clamp } from '../../modules/Clamp.js';
import { KatapultIcon } from './katapult-icon.js';
import { Path } from '../../modules/Path.js';
import { when } from 'lit/directives/when.js';

export class KatapultDialog extends LitElement {
  static get ALERT_CONFIG() {
    return {
      dialog: {
        closeOnEscape: true,
        closeOnOutsideClick: true,
        color: 'var(--secondary-color)',
        maxWidth: 600,
        title: 'Alert'
      },
      button: {
        label: 'Dismiss',
        outlined: true
      },
      template: (config) => html`
        ${config.html ?? html`<div style="text-align: center; white-space: pre-wrap;">${config.text ?? ''}</div>`}
        <katapult-button slot="buttons" config="${JSON.stringify(config.button)}" dialog-dismiss></katapult-button>
      `
    };
  }

  static get CONFIRM_CONFIG() {
    return {
      dialog: {
        title: 'Confirm',
        color: 'var(--secondary-color)',
        closeOnEscape: true,
        maxWidth: 600
      },
      cancelButton: {
        label: 'Cancel',
        outlined: true
      },
      confirmButton: {
        label: 'Confirm'
      },
      template: (config) => html`
        ${config.html ?? html`<div style="text-align: center; white-space: pre-wrap;">${config.text ?? ''}</div>`}
        <katapult-button slot="buttons" config="${JSON.stringify(config.cancelButton)}" dialog-dismiss></katapult-button>
        <katapult-button slot="buttons" config="${JSON.stringify(config.confirmButton)}"></katapult-button>
      `
    };
  }

  static _extendConfig(a, b) {
    // Applies config b to a.
    const res = Path.copy(a);
    for (const key in b) {
      if (b[key] === undefined) continue;
      if (Path.isObject(b[key])) {
        for (const subkey in b[key]) {
          if (b[key][subkey] === undefined) continue;
          a[key] ??= {};
          a[key][subkey] = b[key][subkey];
        }
      } else {
        a[key] = b[key];
      }
    }
    return a;
  }

  /**
   * @typedef {object} KatapultDialogAlertConfig
   * @property {string} text - The text to display in the body of the dialog.
   * @property {string} [title='Alert'] - The title of the dialog.
   * @property {object} [dialog] - A configuration object for the dialog.
   * @property {string} [dialog.color='var(--secondary-color)'] - The color of the dialog.
   * @property {object} [button] - A configuration object for the button.
   */

  /**
   * Create a simple alert dialog
   * @param {string|KatapultDialogAlertConfig} textOrConfig - The text to display in the body of the dialog, or a configuration object.
   * @param {string} [title='Alert'] - The title of the dialog
   * @param {string} [color='var(--secondary-color)'] - The color of the dialog
   * @example KatapultDialog.alert('It is Wednesday, my dudes!')
   * @example KatapultDialog.alert('It is Wednesday, my dudes!', 'Wednesday Alert')
   * @example KatapultDialog.alert('It is Wednesday, my dudes!', 'Wednesday Alert', 'var(--paper-green-500)')
   * @example KatapultDialog.alert({ text: 'It is Wednesday, my dudes!', dialog: {title: 'Wednesday Alert', color: 'var(--paper-green-500)', icon: 'celebration'}, button: {label: 'I love Wednesday', icon: 'favorite'}})
   * @return {KatapultDialog}
   */
  static alert(textOrConfig, title, color) {
    // Get or build user supplied config.
    const _config = Path.isObject(textOrConfig) ? textOrConfig : { text: textOrConfig, dialog: { title, color } };
    // TODO: Remove support for legacy body property.
    if (_config.body) _config.text ??= _config.body;
    // Apply user supplied config options.
    const config = KatapultDialog._extendConfig(KatapultDialog.ALERT_CONFIG, _config);
    // Create and return the dialog.
    return KatapultDialog.open(config);
  }

  /**
   * @typedef {object} KatapultDialogConfirmConfig
   * @property {string} text - The text to display in the body of the dialog.
   * @property {string} [title='Confirm'] - The title of the dialog
   * @property {object} [dialog] - A configuration object for the dialog.
   * @property {string} [dialog.color='var(--secondary-color)'] - The color of the dialog.
   * @property {object} [cancelButton] - A configuration object for the cancel button.
   * @property {object} [confirmButton] - A configuration object for the confirm button.
   */

  /**
   * Opens a simple confirm dialog.
   * The dialog has an additional `confirmed` promise property which resolves to `true` or `false` upon the dialog closing.
   * @param {string|KatapultDialogConfirmConfig} textOrConfig - The text to display in the body of the dialog, or a configuration object.
   * @param {string} [title='Confirm'] - The title of the dialog
   * @param {string} [color='var(--secondary-color)'] - The color of the dialog
   * @example KatapultDialog.confirm('Is it Wednesday?')
   * @example KatapultDialog.confirm('Is it Wednesday?', 'Wednesday Check')
   * @example KatapultDialog.confirm('Is it Wednesday?', 'Wednesday Check', 'var(--paper-green-500)')
   * @example KatapultDialog.confirm({ text: 'Is it Wednesday?', dialog: {title: 'Wednesday Check', color: 'var(--paper-green-500)', icon: 'event'}, cancelButton: {label: 'Alas, it is not'}, confirmButton: {label: 'It is, my dude!', icon: 'celebration'}})
   * @example KatapultDialog.confirm({ text: 'Is it Wednesday?', dialog: {title: 'Wednesday Check', color: 'var(--paper-green-500)', icon: 'event'}, cancelButton: {label: 'Alas, it is not'}, confirmButton: {label: 'It is, my dude!', icon: 'celebration'}}).confirmed.then(res => KatapultDialog.alert(`It is ${res ? 'Wednesday' : 'not Wednesday'}, my dude!`, 'Wednesday Result', 'var(--paper-green-500)'))
   * @return {KatapultDialog}
   */
  static confirm(textOrConfig, title, color) {
    // Get or build user supplied config.
    const _config = Path.isObject(textOrConfig) ? textOrConfig : { text: textOrConfig, dialog: { title, color } };
    // TODO: Remove support for legacy body property.
    if (_config.body) _config.text ??= _config.body;
    // Apply user supplied config options.
    const config = KatapultDialog._extendConfig(KatapultDialog.CONFIRM_CONFIG, _config);
    // Propagate dialog color to confirm button, if unset.
    config.confirmButton.color ??= config.dialog.color;
    // Create the dialog.
    const dialog = KatapultDialog.open(config);
    // Set up a confirmed promise.
    let _confirmedResolve;
    dialog.confirmed = new Promise((resolve) => (_confirmedResolve = resolve));
    // Once the dialog begins to open, add a click callback to the confirm button which resolves the promise as true.
    dialog.openStart.then(() =>
      dialog.buttons[1].addEventListener('click', (e) => {
        _confirmedResolve(true);
        // Close the dialog after confirm since the confirm button is not a "dialog-dismiss" button
        dialog.closeAsync();
      })
    );
    // Resolve the promise as false if any other case as the dialog begins to close.
    dialog.closeStart.then(() => _confirmedResolve(false));
    // Return the dialog to the caller.
    return dialog;
  }

  /**
   * Opens an anonymous dialog.
   * @param {Object} config - A configuration object.
   * @param {Function} config.template - A function which accepts `config` as a parameter and returns a rendered html string.
   * @param {Object} config.dialog - A configuration object for the dialog.
   * @return {KatapultDialog}
   */
  static open(config) {
    // Create the dialog.
    const dialog = new KatapultDialog();
    // Set properties on the dialog.
    dialog.config = config.dialog;
    // Set the dialog's content.
    render(config.template?.(config) ?? '', dialog);
    // Add the dialog to the body.
    document.body.appendChild(dialog);
    // Open the dialog.
    dialog.open();
    // Once the dialog has closed, remove it from the DOM.
    dialog.closeComplete.then(() => document.body.removeChild(dialog));
    // Return the newly created dialog.
    return dialog;
  }

  static properties = {
    _boundaryBounds: { type: Object, state: true },
    boundaryElement: { type: Element },
    _boundaryElement: { type: Element, state: true },
    boundaryPadding: { type: Number },
    _centerBounds: { type: Object, state: true },
    centerElement: { type: Element },
    _centerElement: { type: Element, state: true },
    closeButton: { type: Boolean },
    color: { type: String },
    config: { type: Object },
    debug: { type: Boolean, reflect: true },
    _dialogBounds: { type: Object, state: true },
    draggable: { type: Boolean, reflect: true },
    _dragPosition: { type: Object, state: true },
    height: { type: Number },
    icon: { type: String },
    iconConfig: { type: Object },
    maxWidth: { type: Number },
    maxHeight: { type: Number },
    minWidth: { type: Number },
    minHeight: { type: Number },
    modal: { type: Boolean },
    noHeader: { type: Boolean },
    closeOnEscape: { type: Boolean },
    closeOnOutsideClick: { type: Boolean },
    opened: { type: Boolean, reflect: true },
    persistManualPosition: { type: Boolean },
    size: { type: Number },
    _size: { state: true },
    _slotFlags: { type: Object },
    subtitle: { type: String },
    _targetPosition: { type: Object, state: true },
    textColor: { type: String },
    title: { type: String },
    width: { type: Number }
  };

  static styles = css`
    :host {
      display: contents;
      font-family: Roboto;
      font-size: var(--katapult-dialog-font-size);
      transition: font-size 0.3s;
    }

    :host > * {
      z-index: 9999;
      box-sizing: border-box;
    }

    :host([debug]) dialog {
      outline: 2px solid var(--katapult-dialog-debug-color);
    }

    [hidden] {
      display: none !important;
    }

    dialog {
      /* Position */
      position: fixed;
      top: var(--katapult-dialog-top, 0);
      left: var(--katapult-dialog-left, 0);
      height: var(--katapult-dialog-height);
      width: var(--katapult-dialog-width);
      min-height: var(--katapult-dialog-min-height);
      min-width: var(--katapult-dialog-min-width);
      max-height: var(--katapult-dialog-max-height);
      max-width: var(--katapult-dialog-max-width);
      margin: unset;

      /* Visual */
      display: flex;
      flex-direction: column;
      border-radius: 24px;
      box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.2);
      border: 2px solid var(--katapult-dialog-border-color);
      padding: unset;
      overflow: hidden;
      outline: none;

      /* Animation */
      transition:
        transform 0.3s,
        opacity 0.3s;
      transform: translateY(24px) scale(0.98);
      opacity: 0;
      pointer-events: none;
    }

    dialog.open {
      /* transition: transform 0.3s cubic-bezier(0.1, 1.3, 0.7, 1), opacity 0.3s; */
      transform: none;
      opacity: 1;
      pointer-events: auto;
    }

    dialog::backdrop {
      pointer-events: none;
      transition:
        opacity 0.3s,
        backdrop-filter 0.3s;
      opacity: 0;
      background: rgba(255, 255, 255, 0.5);
    }

    dialog.open::backdrop {
      opacity: 1;
      backdrop-filter: blur(4px);
    }

    #header {
      display: flex;
      justify-content: space-between;
      gap: 0.5em;
      background: var(--katapult-dialog-background);
      color: var(--katapult-dialog-color);
      border-bottom: 2px solid var(--katapult-dialog-border-color);
      padding: 0.25em;
      box-sizing: border-box;
    }

    :host([draggable]) #header {
      cursor: grab;
    }

    #header .content {
      display: flex;
      align-items: center;
      gap: 0.5em;
      width: fit-content;
    }

    #headerCenter {
      justify-content: center;
      flex-grow: 1;
    }

    #header .sideWrapper {
      min-width: var(--katapult-dialog-header-side-width);
    }

    #titleContainer {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25em;
    }

    #title,
    #subtitle {
      display: flex;
    }

    #title {
      font-weight: 500;
      font-size: calc(var(--katapult-dialog-font-size) + 2pt);
    }

    #subtitle {
      font-weight: 300;
      font-size: calc(var(--katapult-dialog-font-size) - 1pt);
      opacity: 0.9;
    }

    #body {
      flex-grow: 1;
    }

    ::slotted([slot='body']) {
      width: -webkit-fill-available;
      max-width: -webkit-fill-available;
    }

    #buttons {
      display: flex;
      align-items: stretch;
      justify-content: var(--katapult-dialog-buttons-justify-content);
      gap: 0.5em;
      padding: 1em;
    }
  `;

  render() {
    return html`
      <!-- Dialog Element -->
      <dialog>
        ${when(
          !this.noHeader,
          () => html`
            <!-- Header -->
            <div id="header">
              <div id="headerLeftWrapper" class="sideWrapper">
                <div id="headerLeft" class="content">
                  <slot name="header-left">
                    <katapult-collapse horizontal .opened=${this.draggable}>
                      <katapult-button
                        icon="${this._dragPosition ? 'lock_reset' : 'drag_indicator'}"
                        iconOnly
                        .color=${this.color}
                        .textColor=${this.textColor}
                        .size=${this._size}
                        .disabled=${!this._dragPosition}
                        noBorder
                        noBackground
                        @click="${this.resetPosition}"
                      ></katapult-button>
                    </katapult-collapse>
                  </slot>
                  <!-- This slot allows you to add a second button in the top left corner to the right of the first one -->
                  <!-- There's an invisible, inactive button here by default because without it, the centering distorts the header buttons. -->
                  <slot name="header-left-end">
                    <katapult-button disabled iconOnly noBackground noBorder></katapult-button>
                  </slot>
                </div>
              </div>
              <div id="headerCenter" class="content">
                <!-- Center Start Slot -->
                <slot name="header-center-start"></slot>
                ${this.icon
                  ? html` <katapult-icon
                      .icon=${this.icon}
                      .color=${this.textColor}
                      .size=${this._size}
                      .config=${this.iconConfig}
                    ></katapult-icon>`
                  : nothing}
                <!-- Title Container -->
                <div id="titleContainer">
                  <div id="title" ?hidden=${!this._slotFlags.title && !this.title}>
                    ${this.title ? html`<span>${this.title}</span>` : nothing}
                    <slot name="title" @slotchange="${this._slotChangeHandler}"></slot>
                  </div>
                  <div id="subtitle" ?hidden=${!this._slotFlags.subtitle && !this.subtitle}>
                    ${this.subtitle ? html`<span>${this.subtitle}</span>` : nothing}
                    <slot name="subtitle" @slotchange="${this._slotChangeHandler}"></slot>
                  </div>
                </div>
                <!-- Center End Slot -->
                <slot name="header-center-end"></slot>
              </div>
              <div id="headerRightWrapper" class="sideWrapper" class="content">
                <div id="headerRight" class="content">
                  <!-- This slot allows you to add a second button in the top right corner to the left of the first one -->
                  <!-- There's an invisible, inactive button here by default because without it, the centering distorts the header buttons. -->
                  <slot name="header-right-start">
                    <katapult-button disabled iconOnly noBackground noBorder></katapult-button>
                  </slot>
                  <slot name="header-right">
                    <katapult-collapse horizontal .opened=${this.closeButton}>
                      <katapult-button
                        icon="close"
                        iconOnly
                        .color=${this.color}
                        .textColor=${this.textColor}
                        .size=${this._size}
                        noBorder
                        noBackground
                        @click="${this.close}"
                      ></katapult-button>
                    </katapult-collapse>
                  </slot>
                </div>
              </div>
            </div>
          `
        )}
        <!-- Before Scroll Content -->
        <slot name="before-scroll"></slot>
        <!-- Body -->
        <slot name="before-body"></slot>
        <katapult-scroll id="body" ?hidden=${!this._slotFlags.default}>
          <slot @slotchange="${this._slotChangeHandler}"></slot>
        </katapult-scroll>
        <!-- After Scroll Content -->
        <slot name="after-scroll"></slot>
        <!-- Buttons -->
        <div id="buttons" ?hidden=${!this._slotFlags.buttons}>
          <slot name="buttons" @slotchange="${this._buttonSlotChanged}"></slot>
        </div>
      </dialog>

      <!-- Debugging Containers -->
      ${this.debug
        ? html`
            <!-- Boundary Container -->
            <div
              style=${styleMap({
                position: 'fixed',
                border: '2px dashed var(--katapult-dialog-debug-color)',
                opacity: 0.5,
                pointerEvents: 'none',
                top: `${this._boundaryBounds?.yMin}px`,
                left: `${this._boundaryBounds?.xMin}px`,
                height: `${this._boundaryBounds?.height}px`,
                width: `${this._boundaryBounds?.width}px`
              })}
            >
              <div
                style=${styleMap({
                  height: '100%',
                  width: '100%',
                  opacity: 0.5,
                  outline: `${this.boundaryPadding}px solid var(--katapult-dialog-debug-color)`
                })}
              ></div>
            </div>

            <!-- Center Container -->
            <div
              style=${styleMap({
                position: 'fixed',
                border: '2px solid var(--katapult-dialog-debug-color)',
                opacity: 0.5,
                background: `
            linear-gradient(to top left,
                rgba(0,0,0,0) 0%,
                rgba(0,0,0,0) calc(50% - 2px),
                var(--katapult-dialog-debug-color) 50%,
                rgba(0,0,0,0) calc(50% + 2px),
                rgba(0,0,0,0) 100%),
            linear-gradient(to top right,
                rgba(0,0,0,0) 0%,
                rgba(0,0,0,0) calc(50% - 2px),
                var(--katapult-dialog-debug-color) 50%,
                rgba(0,0,0,0) calc(50% + 2px),
                rgba(0,0,0,0) 100%);
            `,
                pointerEvents: 'none',
                top: `${this._centerBounds?.yMin}px`,
                left: `${this._centerBounds?.xMin}px`,
                height: `${this._centerBounds?.height}px`,
                width: `${this._centerBounds?.width}px`,
                boxSizing: 'border-box'
              })}
            ></div>

            <!-- Target Position Dot -->
            <katapult-icon
              icon="open_with"
              style=${styleMap({
                position: 'fixed',
                color: 'var(--katapult-dialog-debug-color)',
                outline: '2px solid var(--katapult-dialog-debug-color)',
                background: 'white',
                borderRadius: '100%',
                pointerEvents: 'none',
                top: `calc(${this._targetPosition?.y}px - 12px)`,
                left: `calc(${this._targetPosition?.x}px - 12px)`
              })}
            ></katapult-icon>
          `
        : nothing}
    `;
  }

  constructor() {
    super();

    // Prevent default tooltip from showing.
    this.removeAttribute('title');

    // Set property defaults.
    this.color = 'var(--secondary-color)';
    this.opened = this.draggable = false;
    this._centerElement = this._boundaryElement = window;
    this.boundaryPadding = 16;
    this.minWidth = 300;
    this.minHeight = 130;
    this.maxWidth = this.maxHeight = Infinity;
    this._transitionSpeed = 300;
    this._slotFlags = {};

    /** @type { Boolean } */
    this.noHeader = false;

    // Set up the confirmed property.
    /** @type {Promise<boolean>|null} */
    this.confirmed = null;

    // Initialize promises.
    this._openStartPromise = new Promise((x) => (this._openStartResolve = x));
    this._openCompletePromise = new Promise((x) => (this._openCompleteResolve = x));
    this._closeStartPromise = new Promise((x) => (this._closeStartResolve = x));
    this._closeCompletePromise = new Promise((x) => (this._closeCompleteResolve = x));

    // Setup resize observer.
    this._resizeObserver = new ResizeObserver((entries) => {
      // Loop through resized elements.
      for (const entry of entries) {
        // Get the element.
        const elem = entry.target;
        // Set dialog bounds.
        if (elem == this._dialog) {
          this._dialogBounds = this._calcBounds(this._calcRect(elem));
        }
        // Set center bounds.
        if (elem == this._centerElement) {
          this._centerBounds = this._calcBounds(this._calcRect(elem));
        }
        // Set boundary bounds.
        if (elem == this._boundaryElement) {
          this._boundaryBounds = this._calcBounds(this._calcRect(elem), this.boundaryPadding);
        }
        if (elem == this._headerLeft || elem == this._headerRight) {
          this.style.setProperty(
            '--katapult-dialog-header-side-width',
            `${Math.max(this._headerLeft.offsetWidth, this._headerRight.offsetWidth)}px`
          );
        }
      }
    });

    this._keydownHandler = function (e) {
      if (e.key == 'Escape' && this.isActiveDialog) this.closeOnEscape ? this.closeAsync() : this.pulse();
    }.bind(this);

    this._mouseDownHandler = function (e) {
      e.stopPropagation();
      // Determine if the click originated from or within the drag handle.
      const dragZone = e.composedPath().some((x) => x == this._header);
      // If click came from drag handle, begin to drag the dialog.
      if (this.draggable && dragZone) {
        // Call prevent default to keep from selecting text on the page as you drag the dialog.
        e.preventDefault();
        // Get coords of the mouse down event.
        const coords = GetEventCoords(e);
        // let rect = this._calcRect(this);
        // if (!this._dragStartBounds) this._set_dragStartBounds(this._calcBounds(rect));
        // Calc offset between mouse down coords and current dialog position.
        this._dragOffset = { x: this._targetPosition.x - coords.clientX, y: this._targetPosition.y - coords.clientY };
        // Attach mouse move handlers.
        window.addEventListener('mousemove', this._mouseMoveHandler);
        window.addEventListener('touchmove', this._mouseMoveHandler);
      }
      // Store whether or not the event occurred on the dialog.
      this._mousedDownOnDialog = e.composedPath().some((x) => x == this._dialog);
      // Store the button the event occurred within if there is one.
      this._mouseDownButton = e.composedPath().find((x) => x.tagName?.includes('BUTTON'));
    }.bind(this);

    this._mouseUpHandler = function (e) {
      e.stopPropagation();
      // Remove mouse move handlers.
      window.removeEventListener('mousemove', this._mouseMoveHandler);
      window.removeEventListener('touchmove', this._mouseMoveHandler);
      // Clear dragging flag.
      this.dragging = false;
      // See if the user clicked outside of the dialog (starting and ending off of the dialog).
      let mouseUpOnDialog = e.composedPath().some((x) => x == this._dialog);
      let outsideClick = !mouseUpOnDialog && !this._mousedDownOnDialog;
      if (outsideClick && this.isActiveDialog) this.closeOnOutsideClick ? this.closeAsync() : this.pulse();
      // See if the user clicked a button (starting and ending on the same button).
      let mouseUpButton = e.composedPath().find((x) => x.tagName?.includes('BUTTON'));
      let dialogButtonClick =
        this.contains(mouseUpButton) &&
        mouseUpButton == this._mouseDownButton &&
        ['dialog-dismiss', 'dialog-confirm'].some((attr) => mouseUpButton.hasAttribute(attr));
      // Close the dialog if an applicable button was pressed.
      if (dialogButtonClick) this.closeAsync();
    }.bind(this);

    this._mouseMoveHandler = function (e) {
      e.stopPropagation();
      // Get coords of the mouse move event.
      const coords = GetEventCoords(e);
      // Set drag position.
      this._dragPosition = { x: coords.clientX + this._dragOffset.x, y: coords.clientY + this._dragOffset.y };
      // Set dragging flag.
      this.dragging = true;
      // Set manually positioned flag.
      // this
    }.bind(this);

    this._resizeHandler = function (e) {
      // Update center element bounds.
      this._centerBounds = this._calcBounds(this._calcRect(this._centerElement));
      // Update boundary element bounds.
      this._boundaryBounds = this._calcBounds(this._calcRect(this._boundaryElement), this.boundaryPadding);
    }.bind(this);

    // Choose a debug color.
    const debugColors = ['red', 'blue', 'green'];
    window._katapultDialogIndex ??= 0;
    this.style.setProperty('--katapult-dialog-debug-color', debugColors[window._katapultDialogIndex++ % debugColors.length]);
  }

  connectedCallback() {
    super.connectedCallback();

    this._paletteAppliedHandler = () => this._updateColors();

    // Update colors when app configuration reports that the palette has been applied.
    window.addEventListener('palette-applied', this._paletteAppliedHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('palette-applied', this._paletteAppliedHandler);
    // Make sure that the dialog is cleaned up.
    this._cleanupDialog();
  }

  get buttons() {
    // Return all buttons that are not hidden (as determined by offsetParent != null);
    const slot = this.renderRoot.querySelector('slot[name="buttons"]');
    if (!slot) return [];
    const elements = slot?.assignedElements().filter((x) => x instanceof Element) ?? [];
    const visibleElements = elements.filter((x) => {
      const style = window.getComputedStyle(x);
      const isHidden = style.display == 'none';
      return !isHidden;
    });
    return visibleElements;
  }

  get isActiveDialog() {
    // Get list of all dismissable elements.
    const elements = Array.from(window._dismissElementStack);
    // Filter down to only the modal elements.
    const modalElements = elements.filter((x) => x.modal);
    // Prioritize modal elements above non-modal.
    return (modalElements.length ? modalElements : elements).slice(-1)[0] == this;
  }

  get openStart() {
    return this._openStartPromise;
  }

  get openComplete() {
    return this._openCompletePromise;
  }

  get closeStart() {
    return this._closeStartPromise;
  }

  get closeComplete() {
    return this._closeCompletePromise;
  }

  get position() {
    return structuredClone(this._targetPosition);
  }

  // set position(coordinates) {
  //   if (!coordinates || !coordinates?.x || !coordinates?.y) {
  //     this._targetPosition = null;
  //     return;
  //   }

  //   this._targetPosition = coordinates;
  //   this.setPosition(coordinates);
  // }

  firstUpdated() {
    // Store a reference to the dialog.
    this._dialog = this.renderRoot.querySelector('dialog');
    this._dialog.addEventListener('close', () => {
      // The <dialog> element's default modal behavior is to close automatically when the user presses the Esc key. This allows the dialog to override that default behavior if the closeOnEscape property is specifically set to false.
      if (this.modal && this.closeOnEscape === false) {
        this._dialog.showModal();
        return;
      }
      this.opened = false;
    });
    this._dialog.addEventListener('cancel', (e) => e.preventDefault());
    // Store a reference ot the header.
    this._header = this.renderRoot.querySelector('#header');
    this._headerLeft = this.renderRoot.querySelector('#headerLeft');
    this._headerRight = this.renderRoot.querySelector('#headerRight');
    this._headerLeftWrapper = this.renderRoot.querySelector('#headerLeftWrapper');
    this._headerRightWrapper = this.renderRoot.querySelector('#headerRightWrapper');
  }

  willUpdate(changedProperties) {
    // Update properties from config.
    if (changedProperties.has('config')) {
      // Allow config object to set any public property.
      for (const prop in KatapultDialog.properties) {
        // Skip any private properties.
        if (KatapultDialog.properties[prop].state || prop == 'config') continue;
        if (this.config?.[prop] != null || changedProperties.get('config')?.[prop] != null) this[prop] = this.config[prop];
      }
    }

    // Update size.
    this._size = KatapultIcon.calcSize(this.size);
    if (changedProperties.has('_size')) {
      this.style.setProperty('--katapult-dialog-size', `${this._size}px`);
      this.style.setProperty('--katapult-dialog-font-size', `${(Number(this._size) * 2) / 3}px`);
    }

    // Update colors.
    if (['color', 'textColor'].some((x) => changedProperties.has(x))) {
      this._updateColors();
    }

    // Evaluate external boundary and center element properties.
    ['boundaryElement', 'centerElement'].forEach((prop) => {
      if (changedProperties.has(prop)) {
        const val = this[prop];
        this[`_${prop}`] = val instanceof Element || val instanceof Window ? val : window;
      }
    });

    // Evaluate internal boundary and center element properties.
    ['_boundaryElement', '_centerElement'].forEach((prop) => {
      // Only react to changes if the dialog is open.
      if (changedProperties.has(prop) && this.opened) {
        // Stop watching the previous element.
        this._destroyResizeObserver(changedProperties.get(prop));
        // Begin watching the new element.
        this._createResizeObserver(this[prop]);
      }
    });

    // Update target position.
    if (['_centerBounds', '_dragPosition'].some((x) => changedProperties.has(x))) {
      this._targetPosition = this._dragPosition ?? { x: this._centerBounds?.xMid, y: this._centerBounds?.yMid };
    }

    // Resize dialog.
    const resizeProperties = ['_targetPosition', '_dialogBounds', '_boundaryBounds', 'minHeight', 'minWidth', 'maxHeight', 'maxWidth'];
    if (resizeProperties.some((x) => changedProperties.has(x))) {
      if (!resizeProperties.every((prop) => this[prop] != null)) {
        if (this.debug) console.warn('Not all resize properties are defined.  Skipping resize...');
      } else {
        // Calc top and left dialog position and limit such that the dialog never shrinks beyond minWidth and minHeight.
        const halfDialogHeight = this._dialogBounds.height / 2;
        const halfDialogWidth = this._dialogBounds.width / 2;
        // Clamp top value within permitted bounds.
        const top = Clamp(this._targetPosition.y - halfDialogHeight, this._boundaryBounds.yMin, this._boundaryBounds.yMax - this.minHeight);
        // Clamp left value within permitted bounds.
        const left = Clamp(this._targetPosition.x - halfDialogWidth, this._boundaryBounds.xMin, this._boundaryBounds.xMax - this.minWidth);

        // Clamp drag position so it doesn't exceed maximum compression of the dialog.
        const halfMinHeight = this.minHeight / 2;
        const halfMinWidth = this.minWidth / 2;
        this._targetPosition = {
          y: Clamp(this._targetPosition.y, this._boundaryBounds.yMin + halfMinHeight, this._boundaryBounds.yMax - halfMinHeight),
          x: Clamp(this._targetPosition.x, this._boundaryBounds.xMin + halfMinWidth, this._boundaryBounds.xMax - halfMinWidth)
        };

        // Calculate the maximum allowed height and width of the dialog.
        const maxHeight = Math.min(
          (this._targetPosition.y - this._boundaryBounds.yMin) * 2,
          (this._boundaryBounds.yMax - this._targetPosition.y) * 2,
          this.maxHeight
        );
        const maxWidth = Math.min(
          (this._targetPosition.x - this._boundaryBounds.xMin) * 2,
          (this._boundaryBounds.xMax - this._targetPosition.x) * 2,
          this.maxWidth
        );

        // Set style properties.
        this.style.setProperty('--katapult-dialog-top', `${top}px`);
        this.style.setProperty('--katapult-dialog-left', `${left}px`);
        this.style.setProperty('--katapult-dialog-height', this.height ? `${this.height}px` : 'fit-content');
        this.style.setProperty('--katapult-dialog-width', this.width ? `${this.width}px` : 'fit-content');
        this.style.setProperty('--katapult-dialog-min-height', `${this.minHeight}px`);
        this.style.setProperty('--katapult-dialog-min-width', `${this.minWidth}px`);
        this.style.setProperty('--katapult-dialog-max-height', `${maxHeight}px`);
        this.style.setProperty('--katapult-dialog-max-width', `${maxWidth}px`);
      }
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('opened')) this.opened ? this._openStart() : this._closeStart();
    // Update dialog class.
    this._dialog.classList[this.opened ? 'add' : 'remove']('open');
  }

  _buttonSlotChanged(e) {
    setTimeout(() => {
      this.style.setProperty('--katapult-dialog-buttons-justify-content', this.buttons.length > 1 ? 'space-between' : 'flex-end');
    });
    this._slotChangeHandler(e);
  }

  _calcBounds(rect, offset = 0) {
    if (rect == null) return;
    const xMin = rect.left + offset;
    const xMax = rect.left + rect.width - offset;
    const width = xMax - xMin;
    const xMid = xMin + width / 2;
    const yMin = rect.top + offset;
    const yMax = rect.top + rect.height - offset;
    const height = yMax - yMin;
    const yMid = yMin + height / 2;
    return { xMin, xMax, width, xMid, yMin, yMax, height, yMid };
  }

  _calcRect(elem) {
    if (elem instanceof Element) return elem.getBoundingClientRect();
    if (elem instanceof Window)
      return {
        bottom: window.innerHeight,
        height: window.innerHeight,
        left: 0,
        right: window.innerWidth,
        top: 0,
        width: window.innerWidth,
        x: 0,
        y: 0
      };
  }

  _cleanupDialog() {
    if (this._setup) {
      // Remove dialog from the dismiss element stack.
      window._dismissElementStack.delete(this);
      // Remove click event listener.
      window.removeEventListener('mousedown', this._mouseDownHandler);
      window.removeEventListener('touchstart', this._mouseDownHandler);
      window.removeEventListener('mouseup', this._mouseUpHandler);
      window.removeEventListener('touchend', this._mouseUpHandler);
      // Remove keydown listener.
      window.removeEventListener('keydown', this._keydownHandler);
      // Remove window resize listener.
      window.removeEventListener('resize', this._resizeHandler);
      // Remove element resize observers.
      this._destroyResizeObserver(this._dialog);
      this._destroyResizeObserver(this._headerLeft);
      this._destroyResizeObserver(this._headerRight);
      this._destroyResizeObserver(this._centerElement);
      this._destroyResizeObserver(this._boundaryElement);
      // Clear _setup flag.
      this._setup = false;
    }
  }

  close() {
    this.opened = false;
  }

  closeAsync() {
    // Note: We sometimes want the dialog to be closed asynchronously such as when we are closing due to a katapult-button[dialog-dismiss].
    // Closing asynchronously allows any click events attached to the button to run before we begin closing the dialog.
    setTimeout(() => this.close());
  }

  _closeComplete() {
    // Cleanup the dialog.
    this._cleanupDialog();

    // TODO: This should also be a function.
    // Close the dialog element.
    this._dialog.close();
    // Reset the dialog's position.
    if (!this.persistManualPosition) this.resetPosition(true);

    // Resolve close complete promise.
    this._closeCompleteResolve();
    // Create new close complete promise.
    this._closeCompletePromise = new Promise((x) => (this._closeCompleteResolve = x));
    // Dispatch closed event.
    this.dispatchEvent(new CustomEvent('closed'));
  }

  _closeStart() {
    // Cancel pending open complete timeout.
    if (this._openCompleteTimeout) window.clearTimeout(this._openCompleteTimeout);
    // Dispatch closing event.
    if (this.dispatchEvent(new CustomEvent('closing', { cancelable: true }))) {
      // Resolve open start promise.
      this._closeStartResolve();
      // Create new close start promise.
      this._closeStartPromise = new Promise((x) => (this._closeStartResolve = x));
      // Set timeout to resolve close complete.
      this._closeCompleteTimeout = setTimeout(() => this._closeComplete(), this._transitionSpeed);
    } else {
      this.open();
    }
  }

  _createResizeObserver(elem) {
    if (elem instanceof Element) {
      this._resizeObserver.observe(elem);
    }
  }
  x;

  _destroyResizeObserver(elem) {
    if (elem instanceof Element) {
      this._resizeObserver.unobserve(elem);
    }
  }

  _slotChangeHandler(e) {
    const slot = e.currentTarget;
    this._slotFlags[slot.getAttribute('name') ?? 'default'] = e.currentTarget.assignedElements().length > 0;
  }

  open() {
    this.opened = true;
  }

  _openComplete() {
    // Resolve open complete promise.
    this._openCompleteResolve();
    // Create new open complete promise.
    this._openCompletePromise = new Promise((x) => (this._openCompleteResolve = x));
    // Dispatch opened event.
    this.dispatchEvent(new CustomEvent('opened'));
  }

  _openStart() {
    // Setup the dialog.
    this._setupDialog();
    // Ask the dialog to open.
    if (!this._dialog.open) this._dialog[this.modal ? 'showModal' : 'show']();
    // Cancel pending close complete timeout.
    if (this._closeCompleteTimeout) window.clearTimeout(this._closeCompleteTimeout);
    // Dispatch opening event.
    if (this.dispatchEvent(new CustomEvent('opening', { cancelable: true }))) {
      // Resolve open start promise.
      this._openStartResolve();
      // Create new open start promise.
      this._openStartPromise = new Promise((x) => (this._openStartResolve = x));
      // Set timeout to resolve open complete.
      this._openCompleteTimeout = setTimeout(() => this._openComplete(), this._transitionSpeed);
    } else {
      this.close();
    }
  }

  pulse() {
    this._dialog.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }], {
      duration: 100,
      easing: 'ease-out'
    });
  }

  async resetPosition(immediate) {
    const transitionSpeed = immediate === true ? 0 : undefined;
    if (this._centerBounds) await this.setPosition({ y: this._centerBounds.yMid, x: this._centerBounds.xMid }, transitionSpeed);
    this._dragPosition = null;
  }

  async setPosition(targetPosition, transitionSpeed = this._transitionSpeed) {
    // Javascript animate the dialog from it's current position to the new position.
    // We use javascript because then any dimensional changes in the dialog animate nicely.
    // Capture the current dialog position.
    const startPosition = this._targetPosition;
    // Capture the current time.
    const startTime = performance.now();
    // Define a timing function for the animation.
    const timing = (t) => {
      // Emulate the ease-in-out function.
      // https://css-tricks.com/emulating-css-timing-functions-javascript
      return (Math.sin((t - 0.5) * Math.PI) + 1) / 2;
    };
    // Run the animation inside of a promise.
    return new Promise((resolve) => {
      // Define a step function to animate the dialog.
      const step = (timestamp) => {
        // Get percentage.
        const t = transitionSpeed > 0 ? Clamp((timestamp - startTime) / transitionSpeed, 0, 1) : 1;
        // Get transition progress.
        const p = timing(t);
        // Set the target position to the new intermediate position.
        this._targetPosition = {
          y: startPosition.y + (targetPosition.y - startPosition.y) * p,
          x: startPosition.x + (targetPosition.x - startPosition.x) * p
        };
        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      // Begin the animation.
      requestAnimationFrame(step);
    });
  }

  _setupDialog() {
    if (!this._setup) {
      // Add dialog to the dismiss element stack.
      window._dismissElementStack ??= new Set();
      window._dismissElementStack.add(this);
      // Attach click event listener.
      window.addEventListener('mousedown', this._mouseDownHandler);
      window.addEventListener('touchstart', this._mouseDownHandler);
      window.addEventListener('mouseup', this._mouseUpHandler);
      window.addEventListener('touchend', this._mouseUpHandler);
      // Attach keydown listener.
      window.addEventListener('keydown', this._keydownHandler);
      // Attach window resize listener.
      window.addEventListener('resize', this._resizeHandler);
      // Attach element resize observers.
      this._createResizeObserver(this._dialog);
      this._createResizeObserver(this._headerLeft);
      this._createResizeObserver(this._headerRight);
      this._createResizeObserver(this._centerElement);
      this._createResizeObserver(this._boundaryElement);
      // Manually call the resize handler to initialize bounds (since it will only be called on change).
      this._resizeHandler();
      // Set _setup flag.
      this._setup = true;
    }
  }

  _updateColors() {
    // Update properties.
    this.style.setProperty('--katapult-dialog-background', this.color);
    this.style.setProperty('--katapult-dialog-color', this.textColor ?? KatapultButton.GetTextColor(this.color, this));
    this.style.setProperty('--katapult-dialog-border-color', KatapultButton.GetBorderColor(this.color, this));
  }
}

customElements.define('katapult-dialog', KatapultDialog);
